import React, { useState } from "react";

import { PatientPhoto, PinIcon } from "../patient/PatientPanelV4";
import { type TierBadge } from "../common/sequence/RegistrationV4";
import {
  REGISTRATION_MENU,
  getPatientSummary,
  type PatientDetail,
  type RegistrationMenuKey,
} from "./registrationModelV4";

import "./_hisv4-registration.scss";

export type ViewRegistrationV4Props = {
  // data
  patient?: PatientDetail;
  /** undefined = ยังไม่โหลด, null = ยังไม่เป็นสมาชิก */
  tier?: TierBadge | null;
  activeMenu: RegistrationMenuKey;
  // callback
  onSelectMenu: (menu: RegistrationMenuKey) => void;
  onBack: () => void;
  children: React.ReactNode;
};

type MenuItem = (typeof REGISTRATION_MENU)[number];

/** หน้าผู้ป่วยที่ยังไม่มี encounter: ข้อมูลผู้ป่วย + เมนู + เนื้อหา (โครงเดียวกับ PatientPanelV4) */
const ViewRegistrationV4 = (props: ViewRegistrationV4Props) => {
  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState<RegistrationMenuKey[]>([]);

  const patient = getPatientSummary(props.patient ?? {});

  const pinnedItems = REGISTRATION_MENU.filter((item) => pinned.includes(item.key));
  const otherItems = REGISTRATION_MENU.filter((item) => !pinned.includes(item.key));

  const handleTogglePin = (key: RegistrationMenuKey) => {
    setPinned((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  const renderRow = (item: MenuItem): React.ReactNode => {
    const on = item.key === props.activeMenu;
    const isPinned = pinned.includes(item.key);
    if (collapsed) {
      return (
        <button
          key={item.key}
          type="button"
          className={`hv4-menu-item hv4-menu-item-ico${on ? " hv4-menu-item-on" : ""}`}
          aria-current={on ? "page" : undefined}
          title={item.title}
          aria-label={item.title}
          onClick={() => props.onSelectMenu(item.key)}
        >
          <span aria-hidden="true">{item.icon}</span>
        </button>
      );
    }
    return (
      <div
        key={item.key}
        className={`hv4-menu-item${on ? " hv4-menu-item-on" : ""}`}
        aria-current={on ? "page" : undefined}
      >
        <button
          type="button"
          className="hv4-menu-link"
          onClick={() => props.onSelectMenu(item.key)}
        >
          <span className="hv4-menu-t1">{item.title}</span>
          <span className="hv4-menu-t2">{item.subtitle}</span>
        </button>
        <button
          type="button"
          className={`hv4-menu-pin${isPinned ? " hv4-menu-pin-on" : ""}`}
          aria-pressed={isPinned}
          aria-label={`${isPinned ? "เลิกปักหมุด " : "ปักหมุด "}${item.title}`}
          title={isPinned ? "เลิกปักหมุด" : "ปักหมุดเมนูนี้ไว้บนสุด"}
          onClick={() => handleTogglePin(item.key)}
        >
          <PinIcon pinned={isPinned} />
        </button>
      </div>
    );
  };

  return (
    <div className="hv4 hv4-workspace">
      <aside aria-label="ข้อมูลผู้ป่วย" className="hv4-patient-panel">
        <button type="button" className="hv4-back-btn" onClick={props.onBack}>
          ← กลับไปค้นหา
        </button>

        <PatientPhoto src={patient.image || undefined} />

        <div role="group" aria-label="HN และชื่อผู้รับบริการ" className="hv4-id-card">
          <div className="hv4-id-label">HN</div>
          <div className="hv4-id-hn">{patient.hn || "-"}</div>
          <div className="hv4-id-name">{patient.name || "-"}</div>
        </div>

        <div role="group" aria-label="ระดับสมาชิก" className="hv4-mem-card">
          <div className="hv4-mem-label">ระดับสมาชิก</div>
          {props.tier === null && <div className="hv4-mem-value">ยังไม่เป็นสมาชิก</div>}
          {props.tier && (
            <span className={`hv4-mem-tier hv4-mem-tier-${props.tier.tierIndex}`}>
              🏅 {props.tier.tier}
            </span>
          )}
        </div>

        <div className="hv4-p-rows">
          {patient.rows.map((row) => (
            <div key={row.key} className="hv4-p-row">
              <span className="hv4-p-row-key">{row.key}</span>
              <span className="hv4-p-row-value">{row.value}</span>
            </div>
          ))}
        </div>
      </aside>

      <nav aria-label="เมนูผู้ป่วย" className={`hv4-menu${collapsed ? " hv4-menu-collapsed" : ""}`}>
        <div className="hv4-menu-head">
          {collapsed ? null : <span>เมนู</span>}
          <button
            type="button"
            className="hv4-menu-collapse"
            aria-expanded={!collapsed}
            aria-label={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
            title={collapsed ? "ขยายเมนู" : "ย่อเมนู · เพิ่มพื้นที่เนื้อหา"}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>

        {pinnedItems.length > 0 && (
          <>
            <div className="hv4-menu-group">{collapsed ? null : "ปักหมุด"}</div>
            {pinnedItems.map(renderRow)}
          </>
        )}
        {otherItems.map(renderRow)}
      </nav>

      <main className="hv4-main">{props.children}</main>
    </div>
  );
};

export default ViewRegistrationV4;
